import * as XLSX from 'xlsx';

export const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
};

export const formatDateSQL = (dateString: string) => {
  const date = new Date(dateString);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // bulan mulai dari 0
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

type TransformAndGroupOptions = {
  filterKls?: string | number | null;
  filterBln?: string | number | null;
  filterThn?: string | number | null;
};

export const cekTipeDataJson = (data : any) => {
    if (typeof data === "object" && data !== null){
      return data;
    }

    if(typeof data === "string"){
        try {
            return JSON.parse(data);
        }
        catch (e) {
            return data;
        }
    }
}

export const transformAndGroup = (data: any[] = [], opts: TransformAndGroupOptions = {}) => {
  const inputData = Array.isArray(data) ? data : [];
  const { filterKls = null, filterBln = null, filterThn = null } = opts;

  // Helper Functions
  const safeNumber = (v: unknown) => {
    if (v === null || v === undefined || v === "") return 0;
    if (typeof v === "number") return v;
    const cleaned = String(v).replace(/[^0-9.-]/g, "");
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  };
  const toDecimal = (v: unknown) => safeNumber(v).toLocaleString('id-ID', { style: 'decimal' });
  const extractKecamatan = (alamat: string) => {
    if (!alamat) return "";
    return alamat.match(/KEC(?:AMATAN)?\s+(.*?)\s+DESA/i)?.[1] || "";
  };

  // 1) Filter Data
  const filtered = inputData.filter(item => {
    if (!item) return false;
    const itemYear = item.periode?.substring(0, 4);
    const itemMonth = item.periode?.substring(4, 6);

    if (filterKls && String(item.KLASF) !== String(filterKls)) return false;
    if (filterBln && String(itemMonth) !== String(filterBln).padStart(2, '0')) return false;
    if (filterThn && String(itemYear) !== String(filterThn)) return false;
    return true;
  });

  // 2) Grouping (Object Bersarang Satu Tingkat) & Kalkulasi Total
  const groupedData: Record<string, any[]> = {};
  const grandTotalsRaw = { hair: 0, adm: 0, jml: 0, dnd: 0, ttl: 0 };

  filtered.forEach((item) => {
    const rawKey = item.KLASF || "LPP"; // Pengelompokan aslinya hanya berdasar KLASF
    const uniqueId = `${item.NOSAMW}-${item.periode}`; 

    if (!groupedData[rawKey]) {
      groupedData[rawKey] = [];
    }

    const hair = safeNumber(item.R1) + safeNumber(item.R2) + safeNumber(item.R3) + safeNumber(item.R4);
    const adm = safeNumber(item.ADM);
    const dnd = safeNumber(item.DENDA);
    const jml = hair + adm;
    const ttl = jml + dnd;

    // Simpan ke dalam grup
    groupedData[rawKey].push({
      ...item,
      calc: { hair, adm, jml, dnd, ttl, uniqueId }
    });

    // Tambah ke Grand Total
    grandTotalsRaw.hair += hair;
    grandTotalsRaw.adm += adm;
    grandTotalsRaw.jml += jml;
    grandTotalsRaw.dnd += dnd;
    grandTotalsRaw.ttl += ttl;
  });

  // 3) Flatten Object menjadi Array Table Rows (Seperti excelSheetsData di versi 2)
  const tableRows: any[] = [];
  let globalCounter = 1;

  Object.keys(groupedData).sort().forEach(rawKey => {
    const currentGroupData = groupedData[rawKey];
    let subHair = 0, subAdm = 0, subJml = 0, subDnd = 0, subTtl = 0;

    // A. Baris Header Grup
    tableRows.push({ is_group_header: true, label: rawKey });

    // B. Baris Data Item
    currentGroupData.forEach((item) => {
      const c = item.calc;
      subHair += c.hair; subAdm += c.adm; subJml += c.jml; subDnd += c.dnd; subTtl += c.ttl;

      const ket = extractKecamatan(item.ALAMAT);
      const periodeFmt = item.periode ? `${item.periode.substring(0, 4)}-${item.periode.substring(4, 6)}` : "";
      const plggnFmt = item.stat_smb === '30' ? 'Aktif' : item.stat_smb === '40' ? 'Segel' : 'Cabut';

      tableRows.push({
        no: globalCounter++,
        periode: periodeFmt,
        nsb: item.NOSAMW || "",
        nama: item.NAMA || "",
        kls: item.KLASF || "",
        plggn: plggnFmt,
        hair: toDecimal(c.hair),
        adm: toDecimal(c.adm),
        jml: toDecimal(c.jml),
        dnd: toDecimal(c.dnd),
        ttl: toDecimal(c.ttl),
        ket: ket,
        is_summary: false,
        is_group_header: false,
        uniqueId: c.uniqueId
      });
    });

    // C. Baris Sub-Total Grup (Jika datanya lebih dari 1 baris)
    if (currentGroupData.length > 1) {
      tableRows.push({
        no: "", periode: "", nsb: "", nama: "", kls: "", plggn: "", ket: "",
        hair: toDecimal(subHair),
        adm: toDecimal(subAdm),
        jml: toDecimal(subJml),
        dnd: toDecimal(subDnd),
        ttl: toDecimal(subTtl),
        is_summary: true
      });
    }
  });

  // 4) Return Format yang Sama Persis
  return {
    rows: tableRows,
    totals: {
      raw: grandTotalsRaw,
      formatted: {
        shair: toDecimal(grandTotalsRaw.hair),
        sadm: toDecimal(grandTotalsRaw.adm),
        sjml: toDecimal(grandTotalsRaw.jml),
        sdnd: toDecimal(grandTotalsRaw.dnd),
        sttl: toDecimal(grandTotalsRaw.ttl)
      }
    }
  };
};

export const transformAndGroup2 = (data: any[] = [], opts: any = {}) => {
  const inputData = Array.isArray(data) ? data : [];
  const { filterKls = null, filterBln = null, filterThn = null, checkedTransfers = [], isExport = false } = opts;

  const toDecimal = (v: any) => Number(v || 0).toLocaleString('id-ID');
  
  const extractKecamatan = (alamat: string) => {
    if (!alamat) return "TIDAK TERDEFINISI";
    const match = alamat.match(/KEC(?:AMATAN)?(?:\.|\s+)\s*(.*?)\s+DESA/i);
    return match ? match[1].trim().toUpperCase() : alamat;
  };

  const filtered = inputData
    .filter(item => {
      const pRaw = String(item.PERIODE || item.periode || "");
      if (!pRaw || pRaw.length < 6) return false;

      const itemYear = pRaw.substring(0, 4);
      const itemMonth = pRaw.substring(4, 6);

      if (filterThn && itemYear !== String(filterThn)) return false;

      if (filterBln && itemMonth !== String(filterBln).padStart(2, '0')) return false;

      if (filterKls && String(item.KLASF) !== String(filterKls)) return false;

      return true;
    })
    .sort((a, b) => {
      const pA = String(a.PERIODE || a.periode || "");
      const pB = String(b.PERIODE || b.periode || "");

      // Urutkan PERIODE dulu secara ASC
      if (pA !== pB) return pA.localeCompare(pB);

      // Jika PERIODE sama, baru urutkan berdasarkan KLASF
      const kA = String(a.KLASF || "");
      const kB = String(b.KLASF || "");
      return kA.localeCompare(kB);
    });

  const nestedGroups: Record<string, Record<string, any[]>> = {};
  const grandTotalsRaw = { hair: 0, adm: 0, dnd: 0, ttl: 0 };

  filtered.forEach((item) => {
    let kec = extractKecamatan(item.ALAMAT);
    
    // Ini mengembalikan fungsi pengelompokan aslimu (berdasarkan KLASF dan PERIODE)
    const rawKey = `${item.PERIODE || item.periode || ""} - LPP ${item.KLASF || ""}`;
    const uniqueId = `${item.NOSAMW}-${item.periode}`; // Buat ID unik

    if (isExport && checkedTransfers.includes(uniqueId)) {
        kec = `${kec} TRANSFER`;
    }

    if (!nestedGroups[kec]) nestedGroups[kec] = {};
    if (!nestedGroups[kec][rawKey]) nestedGroups[kec][rawKey] = [];

    const hair = Number(item.R1 || 0) + Number(item.R2 || 0) + Number(item.R3 || 0) + Number(item.R4 || 0);
    const adm = Number(item.ADM || 0);
    const dnd = Number(item.DENDA || 0);
    const ttl = hair + adm + dnd;

    nestedGroups[kec][rawKey].push({
      ...item,
      calc: { hair, adm, jml: hair + adm, dnd, ttl, kec, rawKey, uniqueId }
    });

    grandTotalsRaw.hair += hair;
    grandTotalsRaw.adm += adm;
    grandTotalsRaw.dnd += dnd;
    grandTotalsRaw.ttl += ttl;
  });

  const tableRows: any[] = [];
  const excelSheetsData: Record<string, any[]> = {};
  

  Object.keys(nestedGroups).sort().forEach(kec => {
    excelSheetsData[kec] = [];

    let sheetCounter = 1;

    let grandKecHair = 0, grandKecAdm = 0, grandKecDnd = 0, grandKecTtl = 0;

    Object.keys(nestedGroups[kec]).sort().forEach(rawKey => {
      const currentGroupData = nestedGroups[kec][rawKey]; // Ambil data grup saat ini
      let subHair = 0, subAdm = 0, subDnd = 0, subTtl = 0;

      tableRows.push({ is_group_header: true, label: `${kec} | ${rawKey}` });

      currentGroupData.forEach((item) => {
        const c = item.calc;
        subHair += c.hair; subAdm += c.adm; subDnd += c.dnd; subTtl += c.ttl;

        grandKecHair += c.hair; grandKecAdm += c.adm; grandKecDnd += c.dnd; grandKecTtl += c.ttl;

        const currentNo = sheetCounter++; 

        const ket = item.ALAMAT.match(/KEC(?:AMATAN)?(?:\.|\s+)\s*(.*?)\s+DESA/i)?.[1] || item.ALAMAT;

        // Baris data pelanggan (Sama seperti sebelumnya)
        tableRows.push({
          no: currentNo, 
          periode: item.periode, nsb: item.NOSAMW, nama: item.NAMA,
          kls: item.KLASF, plggn: item.stat_smb === '30' ? 'Aktif' : item.stat_smb === '40' ? 'Segel' : item.stat_smb === '20' ? 'Cabut Meter' : item.stat_smb,
          hair: toDecimal(c.hair), adm: toDecimal(c.adm), jml: toDecimal(c.jml),
          dnd: toDecimal(c.dnd), ttl: toDecimal(c.ttl), ket: ket, is_summary: false, is_group_header: false, uniqueId: c.uniqueId
        });

        excelSheetsData[kec].push({
          "NO": currentNo, "PERIODE": item.periode, "NOSAMW": item.NOSAMW, "NAMA": item.NAMA,
          "KLASF": item.KLASF, "STATUS": item.stat_smb === '30' ? 'Aktif' : item.stat_smb === '40' ? 'Segel' : item.stat_smb === '20' ? 'Cabut Meter' : item.stat_smb,
          "HAIR": c.hair, "ADM": c.adm, "JUMLAH" : c.jml, "DENDA": c.dnd, "TOTAL": c.ttl, "ALAMAT": ket
        });
      });

      if (currentGroupData.length > 1) {
        tableRows.push({
          nama: ``,
          hair: toDecimal(subHair), 
          adm: toDecimal(subAdm),
          jml: toDecimal(subHair + subAdm), 
          dnd: toDecimal(subDnd), 
          ttl: toDecimal(subTtl), 
          is_summary: true
        });

        // Ke Excel Sheet
        excelSheetsData[kec].push({
          "NO": "", "PERIODE": "", "NOSAMW": "", "NAMA": "", "KLASF": "", "STATUS": "",
          "HAIR": subHair, "ADM": subAdm, "JUMLAH" : (subHair + subAdm), "DENDA": subDnd, "TOTAL": subTtl, "ALAMAT": ""
        });
      }

      excelSheetsData[kec].push({ "NO": "", "PERIODE": "", "NOSAMW": "", "NAMA": "" });
    });
    excelSheetsData[kec].push({
      "NO": "", 
      "PERIODE": "", 
      "NOSAMW": "", 
      "NAMA": `TOTAL KECAMATAN ${kec}`, 
      "KLASF": "", 
      "STATUS": "",
      "HAIR": grandKecHair, 
      "ADM": grandKecAdm,
      "JUMLAH": grandKecHair + grandKecAdm,
      "DENDA": grandKecDnd, 
      "TOTAL": grandKecTtl, 
      "ALAMAT": ""
    });
  });

  return {
    rows: tableRows,
    dataPerKecamatan: excelSheetsData,
    totals: { /* ... */ }
  };
};

export const handleDownloadExcel = (filteredLppDetail: any[], currentFilters: any) => {
    // 1. Ambil data yang sudah otomatis dipisah-pisah
    const { dataPerKecamatan } = transformAndGroup2(filteredLppDetail, { 
        ...currentFilters,
        isExport: true 
    });

    // console.log(dataPerKecamatan);

    const wb = XLSX.utils.book_new();

    // 2. Konversi per Kecamatan langsung ke Sheet
    Object.keys(dataPerKecamatan).sort().forEach(kecName => {
        // Karena datanya sudah berupa array baris yang rapi (ada header & subtotal), kita langsung convert!
        const excelRows = dataPerKecamatan[kecName];
        
        const ws = XLSX.utils.json_to_sheet(excelRows);
        XLSX.utils.book_append_sheet(wb, ws, kecName.substring(0, 31));
    });

    // 3. Simpan file
    XLSX.writeFile(wb, `LPP_Per_Kecamatan_${new Date().getTime()}.xlsx`);
};